import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Copy } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

export default function OutletQR() {
  const { id } = useParams();
  const [outlet, setOutlet] = useState(null);
  const [qr, setQr] = useState(null); // { url, image_base64 }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, q] = await Promise.all([
          api.get(`/outlets/${id}`),
          api.get(`/outlets/${id}/qr`),
        ]);
        setOutlet(o.data);
        setQr(q.data);
      } catch (e) {
        toast.error("Failed to load QR");
      } finally { setLoading(false); }
    })();
  }, [id]);

  // Build the public URL using current frontend origin (so QR works across environments)
  const publicUrl = outlet ? `${window.location.origin}/r/${outlet.slug}` : "";

  const downloadPNG = () => {
    // Use the backend-rendered PNG (server-side QR with correct domain)
    if (!qr?.image_base64) return;
    const a = document.createElement("a");
    a.href = qr.image_base64;
    a.download = `${outlet?.slug || "qr"}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) return <DashboardLayout><div className="text-[var(--ink-muted)]">Loading…</div></DashboardLayout>;
  if (!outlet) return null;

  return (
    <DashboardLayout
      title={`QR — ${outlet.name}`}
      actions={
        <Link to={`/outlet/${id}`} className="btn-ghost" data-testid="qr-back-btn" style={{ padding: "8px 14px", fontSize: 13 }}>
          <ArrowLeft size={16} weight="bold" /> Back to outlet
        </Link>
      }
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-pop p-8 flex flex-col items-center" style={{ background: "var(--secondary)" }}>
          <div className="bg-white p-6 rounded-2xl border-[1.5px] border-[var(--ink)]">
            <QRCodeSVG value={publicUrl} size={260} bgColor="#ffffff" fgColor="#1A1A1A" level="M" includeMargin={false} />
          </div>
          <div className="mt-5 text-center">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Scans go to</div>
            <div className="mt-1 text-sm font-semibold break-all max-w-xs">{publicUrl}</div>
          </div>
          <div className="mt-5 flex flex-wrap gap-3 justify-center">
            <button onClick={downloadPNG} data-testid="qr-download-btn" className="btn-primary">
              <Download size={16} weight="bold" /> Download PNG
            </button>
            <button onClick={copyLink} data-testid="qr-copy-link-btn" className="btn-ghost">
              <Copy size={16} weight="bold" /> Copy link
            </button>
          </div>
        </div>

        <div className="card-pop p-7">
          <h3 className="display text-xl font-extrabold">How to use this QR</h3>
          <ol className="mt-4 space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full text-white flex items-center justify-center font-bold text-xs flex-shrink-0" style={{ background: "var(--primary)" }}>1</span>
              <span><strong>Download</strong> the PNG and place it on tables, receipts, or near the register.</span>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full text-white flex items-center justify-center font-bold text-xs flex-shrink-0" style={{ background: "var(--primary)" }}>2</span>
              <span>Customers scan with their phone camera — no app needed.</span>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full text-white flex items-center justify-center font-bold text-xs flex-shrink-0" style={{ background: "var(--primary)" }}>3</span>
              <span>They share their feedback, then spin for a chance to win a reward.</span>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full text-white flex items-center justify-center font-bold text-xs flex-shrink-0" style={{ background: "var(--primary)" }}>4</span>
              <span>Staff redeems the unique code from the <strong>Redeem</strong> page.</span>
            </li>
          </ol>

          <div className="mt-7 rounded-xl border-[1.5px] border-[var(--ink)] p-4" style={{ background: "var(--pastel-yellow)" }}>
            <div className="text-xs font-bold uppercase tracking-widest">Pro tip</div>
            <p className="text-sm mt-1">Print 8-10 copies and pre-laminate them — replacing torn stickers later is the #1 reason owners lose review momentum.</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
